import FileSaver from 'file-saver'
import { useCallback, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
	createDeploy,
	generateSql,
	generateSqlPreviewEnvironment,
	getEnvironmentsWithLastDeploymentPerObjectType,
} from 'src/endpoints'

import { MessageModal } from '@/components'
import { Modal } from '@/components/Modal/Modal'
import { DDL_MODAL_ID } from '@/constants'
import { getApiRef, useApiRequest } from '@/endpoints/hooks'
import { useAppContext } from '@/hooks'
import { useSelectedBranchNumericId } from '@/pages/User/pages/Home/components/TreePanel/TreePanelContext'
import { useLazyGenerateAndPushToGitUsingGetQuery } from '@/rtkApi'
import { useGeneratedSql } from '@/utils/sql'

import { CustomFetchBaseQueryError, DdlModalComponentProps } from '../types'
import { generateHeaderTitle } from '../utils'
import { Footer } from './Footer'
import { ModalContent } from './ModalContent'

const DdlModalComponent = ({
	node,
	environment,
	preview,
	drop,
	onClose,
}: DdlModalComponentProps) => {
	const { t } = useAppContext()
	const request = useApiRequest()
	const branchId = useSelectedBranchNumericId()

	const [successShown, setSuccessShown] = useState(false)
	const [deploying, setDeploying] = useState(false)
	const [pushingToGit, setPushingToGit] = useState(false)

	const [sqlPreview, loadingPreview, errorPreview] = useGeneratedSql(
		generateSqlPreviewEnvironment(
			node.id ?? 0,
			environment.id ?? 0,
			environment.objectType?.id ?? 0,
		),
		[node.id, environment.id],
	)

	const [sqlProd, loadingProd, errorProd] = useGeneratedSql(
		generateSql(
			node.id ?? 0,
			environment.id ?? 0,
			environment.objectType?.id as number,
		),
		[node.id, environment.id],
	)

	const { sql, loading } = useMemo(
		() =>
			preview
				? { sql: sqlPreview, loading: loadingPreview, error: errorPreview }
				: { sql: sqlProd, loading: loadingProd, error: errorProd },
		[
			errorPreview,
			errorProd,
			loadingPreview,
			loadingProd,
			preview,
			sqlPreview,
			sqlProd,
		],
	)

	const saveFile = useCallback(
		(fileName: string, blobPart: BlobPart[], type?: string) => {
			const blob = new Blob(blobPart, {
				type: type ?? 'text/plain;charset=utf-8',
			})

			FileSaver.saveAs(blob, fileName)
		},
		[],
	)
	const [triggerGenerateAndPushToGit] =
		useLazyGenerateAndPushToGitUsingGetQuery()

	const handleDownload = useCallback(() => {
		if (sql) {
			saveFile(`${sql.fileName ?? node.name}`, [sql.sql])
		}
	}, [sql, saveFile, node.name])

	const pushToGit = async () => {
		if (branchId == null) return
		setPushingToGit(true)

		try {
			const { error } = await triggerGenerateAndPushToGit({
				branchId,
				structureId: node.id || 0,
				environmentId: environment.id || 0,
				objectSettingsId: environment.objectType?.id as number,
			})

			if (error && 'data' in error) {
				const customError = error as CustomFetchBaseQueryError

				const errorMessage = `${customError?.data.errorCode || 'Unknown Error'}: ${customError?.data.errorDescription || 'No description available'}`
				toast(errorMessage, { type: 'error' })
				return
			}

			toast(t('DEPLOYMENT_GIT_PUSHED'), { type: 'success' })
			onClose()
		} finally {
			setPushingToGit(false)
		}
	}

	const handleDeploy = async () => {
		if (!sql) {
			return
		}

		setDeploying(true)

		await request(
			createDeploy(
				node.id ?? 0,
				environment.id ?? 0,
				environment.objectType?.id as number,
				{
					data: sql.sql,
					description: '-',
					structureHistoryId: sql.structureHistoryId,
					templateId: sql.template.id || 0,
				},
			),
		)

		setDeploying(false)
		setSuccessShown(true)
	}

	const handleInfoClose = async () => {
		getApiRef(
			getEnvironmentsWithLastDeploymentPerObjectType(node.id ?? 0),
		).invalidate()

		onClose()
	}

	return (
		<>
			<Modal
				open
				onClose={onClose}
				stretchFooterButtons={false}
				resizable
				maximized
				maximizeButtonVisible
				dialogId={DDL_MODAL_ID}
				header={generateHeaderTitle(t, drop, preview)}
				contentStyle={{ width: 800, minHeight: 530, minWidth: 530 }}
				footer={
					<Footer
						deploying={deploying}
						handleDeploy={handleDeploy}
						handleDownload={handleDownload}
						onClose={onClose}
						preview={preview}
						pushToGit={pushToGit}
						pushingToGit={pushingToGit}
					/>
				}
			>
				<ModalContent loading={loading} sql={sql} />
			</Modal>

			<MessageModal
				type="info"
				opened={successShown}
				onClose={handleInfoClose}
				title={t('DEPLOYMENT')}
				message={t('DEPLOYMENT_MARK_CREATED')}
			/>
		</>
	)
}

export const DdlModal = DdlModalComponent
